// @flow strict

import * as React from 'react';
import cx from 'classnames';
import styles from './styles.scss';
import { Button } from '../../internal/Button';
import LazyLoadImage from '../../internal/LazyLoadImage';
import img from '../../static/images/deals.png';

type CtaLink = {|
  text: string,
  href: string,
|};

type BackgroundColors = 'White' | 'Silver' | 'Charcoal' | 'Green' | 'Black';

type PropTypes = {|
  background?: BackgroundColors,
  text?: string,
  ctaLinks: Array<CtaLink>,
  theme?: 'Light' | 'Dark',
|};

const findBestTheme = (background?: BackgroundColors) => {
  switch (background) {
    case 'Green':
      return 'Dark';
    case 'Charcoal':
    case 'Black':
      return 'Light';
    default: {
      return 'Dark';
    }
  }
};

const findButtonVariant = (background: BackgroundColors, index: number) => {
  switch (background) {
    case 'Black':
    case 'Charcoal':
      return index % 2 === 0 ? 'White' : 'Citrus';

    case 'Green':
      return index % 2 === 0 ? 'Black' : 'DefaultInverse';

    case 'Silver':
    case 'White':
      return index % 2 === 0 ? 'Black' : 'Citrus';

    default:
      return index % 2 === 0 ? 'Black' : 'Citrus';
  }
};
const DealsCta = ({
  background = 'Silver',
  text,
  theme = findBestTheme(background),
  ctaLinks,
}: PropTypes) => (
  <div
    className={cx(
      styles.block,
      background === 'Black' && styles.black,
      background === 'Silver' && styles.silver,
      background === 'Green' && styles.green,
      background === 'Charcoal' && styles.charcoal,
      background === 'White' && styles.white,
    )}
  >
    <h4 className={cx(styles.titleText)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1334.38 299.8"
        fill={theme === 'Light' ? '#fff' : '#221f20'}
        className={styles.titleImage}
      >
        <path d="M153.37.51c77.92,0,141.08,63.27,141.08,141.33H153.37Z" />
        <path d="M153.37,154.13H294.45V295.45H224.58a71.26,71.26,0,0,1-71.21-71.33h0Z" />
        <ellipse cx="70.55" cy="71.17" rx="70.54" ry="70.66" />
        <path d="M0,224.79a70.59,70.59,0,0,1,70.53-70.66H141.1v70.66A70.55,70.55,0,1,1,0,226.47Q0,225.63,0,224.79Z" />
        <path d="M413,0H514.24c79.58-.83,133.6,57.11,133,148.65.83,91.54-53.19,147.83-133,147H413Zm97.11,268.23c68.66,0,107.63-43.29,107.63-120.4,0-77.32-39-120.2-107.63-120.2H442.49v240.6Z" />
        <path d="M774.37,80.42c50.51-.62,86,41.23,85.36,94.84v16.9h-152c.82,57.94,30.1,84.54,66.18,84.54h2.47c29.07,0,47.42-13.4,55.47-41.44h27.42c-4.54,20.82-14.44,36.7-29.49,47.83s-33.4,16.7-55.46,16.7c-53,.62-94.84-39.79-94.22-110.1C679.32,118.77,724.89,79.8,774.37,80.42Zm57.94,88.65c-1.24-37.73-23.51-65.56-56.29-65.56H771.9c-28.66,0-57.94,17.52-63.3,65.56Z" />
        <path d="M1034.92,295.67c-1.45-7.63-2.06-19-2.06-33.82-8.66,19.59-39,37.94-74.85,37.94-36.49.62-67.62-27.63-66.8-62.47,0-21,7.22-36.49,21.44-46.19C926.88,181.24,944,175.46,964,174l66.6-5v-8.45c0-36.08-20-57.11-52.78-57.11h-2.07c-14.22,0-26.18,3.5-36.08,10.51q-14.53,10.52-16.7,29.69H895.54c1.86-37.11,36.7-63.91,81-63.29,53.19,0,81.44,35.87,81.44,80.2v88.66c0,21,.82,36.49,2.27,46.39Zm-4.33-104.33-61.24,4.33c-30.51,2.27-50.51,17.11-50.51,40,0,23.3,18.56,41,42.06,41h4.33c28.66.82,66.39-27.22,65.36-57.11Z" />
        <path d="M1112.17,0h27.42V295.67h-27.42Z" />
        <path d="M1198.3,232.37c2.68,28.25,24.54,44.33,54.43,44.33h4.33c29.49,0,47.63-14.85,47.63-37.94,0-17.11-9.69-26.18-37.11-32.57l-30.93-6.81c-17.73-3.92-31.75-10.1-42.06-18.76-10.1-8.87-15.26-21.44-15.26-38.14,0-39,34.44-62.06,75.67-62.06,50.72-.62,77.32,31.75,79.38,67.42h-27.63c-2.47-23.71-21.44-44.33-49.69-44.33h-4.33c-28.66,0-46,17.11-46,36.29,0,18.76,10.92,27.83,38.34,33.81l33,7.22c17.12,3.5,30.31,9.69,39.8,18.14s14.22,20.82,14.22,37.52c0,39.8-30.72,63.3-77.11,63.3-53.6,0-82.06-28-84.12-67.42Z" />
      </svg>
      <span className={cx(styles.visuallyhidden)}>Deals</span>
    </h4>
    {text && (
      <p
        className={cx(
          styles.blockText,
          theme === 'Light' && styles.whiteText,
          theme === 'Dark' && styles.blackText,
        )}
      >
        {text}
      </p>
    )}
    {ctaLinks.length > 0 && (
      <div className={styles.links}>
        {ctaLinks.map((ctaLink, index) => (
          <Button
            onClick={() => window.open(ctaLink.href, '_self')}
            variant={findButtonVariant(background, index)}
          >
            {ctaLink.text}
          </Button>
        ))}
      </div>
    )}
    <div>
      <LazyLoadImage
        src={img}
        alt=""
        className={styles.image}
        defaultSize={{ width: 449, height: 235 }}
      />
    </div>
  </div>
);

export default DealsCta;
